import { useEffect, useState, useRef } from 'react';

export default function FinalCTA() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section 
      ref={sectionRef}
      className="relative py-32 overflow-hidden"
      style={{ backgroundColor: '#1E4FFF' }}
    >
      {/* Subtle pattern overlay */}
      <div className="absolute inset-0 opacity-10" style={{
        backgroundImage: `radial-gradient(circle at 1px 1px, rgba(255, 255, 255, 0.15) 1px, transparent 0)`,
        backgroundSize: '40px 40px'
      }}></div>

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        <div className="max-w-4xl mx-auto">
          <div 
            className={`p-12 lg:p-16 rounded-3xl text-center transition-all duration-1000 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
            style={{ 
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
            }}
          >
            <h2 className="text-4xl lg:text-5xl font-semibold mb-6 text-white leading-tight">
              Built for Property Owners.<br />
              Trusted by Investors.<br />
              Designed for Ghana.
            </h2>
            
            <p className="text-xl mb-10 text-white/90 max-w-2xl mx-auto leading-relaxed">
              Join the future of real estate management. Start your journey with FlexDown today.
            </p>

            <div className="flex flex-wrap justify-center gap-4">
              <button 
                className="px-10 py-4 rounded-xl font-medium whitespace-nowrap transition-all duration-300 hover:shadow-2xl hover:scale-105"
                style={{ 
                  backgroundColor: '#FFFFFF',
                  color: '#1E4FFF'
                }}
              >
                Get Started Free
              </button>
              
              <button 
                className="px-10 py-4 rounded-xl font-medium text-white whitespace-nowrap transition-all duration-300 hover:bg-white/20"
                style={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(12px)',
                  border: '1px solid rgba(255, 255, 255, 0.4)'
                }}
              >
                Schedule a Demo
              </button>
            </div>

            {/* Trust indicators */}
            <div className="mt-12 pt-8 border-t border-white/20">
              <div className="flex flex-wrap justify-center items-center gap-8 text-white/80">
                <div className="flex items-center gap-2">
                  <i className="ri-shield-check-line text-2xl"></i>
                  <span className="text-sm">Bank-level Security</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-verified-badge-line text-2xl"></i>
                  <span className="text-sm">Verified Properties</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-customer-service-2-line text-2xl"></i>
                  <span className="text-sm">24/7 Support</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="container mx-auto px-6 lg:px-12 mt-20">
        <div className="text-center text-white/60 text-sm">
          <p>© 2025 FlexDown. All rights reserved.</p>
          <div className="mt-4">
            <a 
              href="https://readdy.ai/?ref=logo" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:text-white transition-colors duration-300"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
